import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Table, Input, DatePicker, App } from 'antd';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '@/services/api.service';

type PortalLine = {
  line_number: number;
  item_code?: string;
  item_description?: string;
  quantity_billed: number;
  unit_price: number;
  amount: number;
};

type PortalPayload = {
  buyer_invoice_number: string;
  supplier_name: string;
  currency: string;
  invoice_status: string;
  portal_submitted_at: string | null;
  header: {
    supplier_invoice_number: string;
    supplier_invoice_date: string | null;
    received_date: string | null;
    due_date: string | null;
    payment_terms: string;
    notes: string;
    subtotal: number;
    discount_amount: number;
    vat_amount: number;
    freight_charges: number;
    other_charges: number;
    grand_total: number;
  };
  lines: PortalLine[];
};

function unwrapPortal<T>(res: unknown): T {
  const r = res as { data?: unknown };
  const outer = r?.data ?? res;
  const inner = (outer as { data?: unknown })?.data ?? outer;
  return inner as T;
}

export default function SupplierInvoicePortalPage() {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<PortalPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    api
      .get(`/procurement/public/supplier-invoice/${token}`)
      .then((res) => {
        const d = unwrapPortal<PortalPayload>(res);
        setData(d);
        form.setFieldsValue({
          supplier_invoice_number: d.header?.supplier_invoice_number,
          supplier_invoice_date: d.header?.supplier_invoice_date ? dayjs(d.header.supplier_invoice_date) : undefined,
          received_date: d.header?.received_date ? dayjs(d.header.received_date) : undefined,
          due_date: d.header?.due_date ? dayjs(d.header.due_date) : undefined,
          payment_terms: d.header?.payment_terms,
          notes: d.header?.notes,
        });
      })
      .catch(() => setError('Invalid or expired link'))
      .finally(() => setLoading(false));
  }, [token, form]);

  const readOnly =
    !data ||
    data.invoice_status !== 'Draft' ||
    !!data.portal_submitted_at;

  const onFinish = (values: Record<string, unknown>) => {
    if (!token || readOnly) return;
    setSubmitting(true);
    api
      .post(`/procurement/public/supplier-invoice/${token}/submit`, {
        supplier_invoice_number: values.supplier_invoice_number,
        supplier_invoice_date: (values.supplier_invoice_date as { format?: (f: string) => string } | undefined)?.format?.(
          'YYYY-MM-DD'
        ),
        received_date: (values.received_date as { format?: (f: string) => string } | undefined)?.format?.('YYYY-MM-DD'),
        due_date: (values.due_date as { format?: (f: string) => string } | undefined)?.format?.('YYYY-MM-DD'),
        payment_terms: values.payment_terms,
        notes: values.notes,
      })
      .then(() => {
        message.success('Your details were submitted. Thank you.');
        setData((prev) =>
          prev
            ? {
                ...prev,
                portal_submitted_at: new Date().toISOString(),
                header: {
                  ...prev.header,
                  supplier_invoice_number: String(values.supplier_invoice_number ?? prev.header.supplier_invoice_number),
                  payment_terms: String(values.payment_terms ?? prev.header.payment_terms ?? ''),
                  notes: String(values.notes ?? prev.header.notes ?? ''),
                },
              }
            : prev
        );
      })
      .catch((e) => message.error(e?.response?.data?.message || 'Submit failed'))
      .finally(() => setSubmitting(false));
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}>Loading...</div>;
  if (error || !data) return <div style={{ padding: 48, textAlign: 'center' }}>{error || 'Not found'}</div>;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      <Card
        title={`Purchase bill (supplier) — ${data.supplier_name || 'Supplier'}`}
        extra={<span style={{ fontWeight: 400 }}>Our ref: {data.buyer_invoice_number}</span>}
      >
        {data.invoice_status !== 'Draft' && (
          <p style={{ marginBottom: 16 }}>This bill is no longer in draft at the buyer and cannot be edited here.</p>
        )}
        {data.portal_submitted_at && data.invoice_status === 'Draft' && (
          <p style={{ marginBottom: 16 }}>
            Submitted on {dayjs(data.portal_submitted_at).format('DD MMM YYYY, HH:mm')}.
          </p>
        )}
        <p style={{ marginBottom: 16 }}>
          Confirm your tax invoice number and dates below. Line quantities and amounts are set by the buyer from GRN data.
        </p>
        <Form form={form} onFinish={onFinish} layout="vertical" disabled={readOnly}>
          <Form.Item name="supplier_invoice_number" label="Your tax invoice number" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="supplier_invoice_date" label="Your invoice date">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="received_date" label="Bill / received date">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="due_date" label="Due date">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="payment_terms" label="Payment terms">
            <Input placeholder="e.g. Net 30" />
          </Form.Item>
          <Form.Item name="notes" label="Notes to buyer">
            <Input.TextArea rows={2} />
          </Form.Item>
          <h4>Lines (read-only)</h4>
          <Table<PortalLine>
            size="small"
            rowKey="line_number"
            dataSource={data.lines}
            pagination={false}
            columns={[
              { title: '#', dataIndex: 'line_number', width: 48 },
              { title: 'Item', dataIndex: 'item_code', width: 100 },
              { title: 'Description', dataIndex: 'item_description', ellipsis: true },
              { title: 'Qty', dataIndex: 'quantity_billed', width: 80, align: 'right' },
              { title: 'Rate', dataIndex: 'unit_price', width: 100, align: 'right', render: (v: number) => Number(v).toFixed(2) },
              {
                title: 'Amount',
                dataIndex: 'amount',
                width: 110,
                align: 'right',
                render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }),
              },
            ]}
            style={{ marginBottom: 16 }}
          />
          <p>
            <strong>Subtotal:</strong> {data.header.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}{' '}
            {data.currency} &nbsp;|&nbsp; <strong>VAT:</strong>{' '}
            {data.header.vat_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} &nbsp;|&nbsp;{' '}
            <strong>Grand total:</strong> {data.header.grand_total.toLocaleString(undefined, { minimumFractionDigits: 2 })}{' '}
            {data.currency}
          </p>
          {!readOnly && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Submit confirmation
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
}
